import { useEffect, useState } from "react";
import { Banknote, Loader2, MoreHorizontal, Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { deletePayment, formatCents, type Invoice, type Payment } from "../../../lib/sales";
import { Btn, Card, CardHead, EmptyState, Modal, fmtDate, useToast } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { PaymentModal } from "./PaymentDialog";
import { defaultPaymentMethods, type CrmPaymentMethods } from "@wa/core/payment-methods";
import { readPaymentMethodsOrDefault } from "../../../lib/paymentSettings";

/**
 * Amount and method, when it arrived, what it was tagged with, the menu.
 *
 * No `listCard(COLS)` goes with it. The floor that helper sets is applied by
 * `.crm-card:has(.crm-list-head)`, and this panel has no head for that selector
 * to match, so the style was inert wherever it was written. The card carries
 * `crm-list-stack` instead, which folds these rows into blocks below 560px.
 */
const COLS = "minmax(0,1.6fr) 1fr minmax(0,1.3fr) 44px";

/**
 * What has been received against an invoice, and the way to record more.
 *
 * A list of objects rather than a matrix, so these are pills. Adding a payment
 * used to be an inline form wedged under the list, which pushed everything down
 * the moment the card had anything in it; it is a dialog now.
 */
export function DocPayments({
  invoice,
  canManage,
  onChange,
}: {
  invoice: Invoice;
  canManage: boolean;
  onChange: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const payments = invoice.payments ?? [];
  const remaining = invoice.totalCents - invoice.paidCents;

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Payment | null>(null);
  const [pendingDel, setPendingDel] = useState<Payment | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * What Settings > Payments offers, fetched by the card rather than by the
   * dialog.
   *
   * The card is on screen well before anybody presses Add, so the picker is
   * populated by the time it opens - no spinner inside a modal, and no control
   * that swaps under the operator's hand halfway through filling it in. The
   * fallback is the default list rather than an error: a settings read that
   * failed must never stop somebody recording money that has arrived.
   */
  const [methods, setMethods] = useState<CrmPaymentMethods>(defaultPaymentMethods);

  useEffect(() => {
    void readPaymentMethodsOrDefault().then(setMethods);
  }, []);

  async function confirmDelete() {
    if (!pendingDel) return;
    setBusy(true);
    try {
      await deletePayment(invoice.id, pendingDel.id);
      setPendingDel(null);
      onChange();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    // `crm-list-stack`: below 560px these rows fold into blocks. Without it the
    // four tracks share out a 313px card and the sum - the one figure the panel
    // exists for - is squeezed into 72px.
    <Card className="mt-4 crm-list-stack">
      <CardHead
        title={t("sales.payments.title")}
        sub={t("sales.payments.paidOfTotal", {
          paid: formatCents(invoice.paidCents, invoice.currency),
          total: formatCents(invoice.totalCents, invoice.currency),
        })}
        actions={
          canManage && remaining > 0 ? (
            <Btn small tone="dark" onClick={() => setShowAdd(true)}>
              <Plus className="size-4" />
              {t("sales.payments.add")}
            </Btn>
          ) : null
        }
      />

      {payments.length === 0 ? (
        <EmptyState icon={Wallet} title={t("sales.payments.empty")} body={t("sales.payments.docEmptyBody")} />
      ) : (
        <div className="crm-rows">
          {payments.map((p) => (
            <div key={p.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
              <span className="flex min-w-0 items-center gap-3">
                {/* A tinted disc, not an avatar: a payment has no name to
                    initialise and inventing initials from a sum is nonsense. */}
                <span className="crm-av crm-av-soft">
                  <Banknote className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="crm-prow-n crm-tnum crm-trunc block">
                    {formatCents(p.amountCents, p.currency)}
                  </span>
                  <span className="crm-prow-s crm-trunc block">{p.method || "-"}</span>
                </span>
              </span>

              <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(p.paidAt)}</span>
              <span className="crm-mut crm-trunc text-[12.5px]" title={p.reference ?? undefined}>
                {p.reference || "-"}
              </span>

              {canManage ? (
                <PaymentRowMenu t={t} onEdit={() => setEditing(p)} onDelete={() => setPendingDel(p)} />
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd ? (
        <PaymentModal
          invoice={invoice}
          remaining={remaining}
          methods={methods}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            onChange();
          }}
        />
      ) : null}

      {editing ? (
        <PaymentModal
          invoice={invoice}
          remaining={remaining}
          methods={methods}
          payment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChange();
          }}
        />
      ) : null}

      {pendingDel ? (
        <Modal
          title={t("common.delete")}
          label={t("common.delete")}
          onClose={() => setPendingDel(null)}
          footer={
            <>
              <Btn onClick={() => setPendingDel(null)} disabled={busy}>
                {t("common.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void confirmDelete()} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                {t("common.delete")}
              </Btn>
            </>
          }
        >
          <p className="crm-mut text-[13.25px] leading-relaxed">{t("sales.payments.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </Card>
  );
}

/** The 44px track at the end of a payment row: correct the row, or drop it.
 *  Deleting still asks first; correcting opens the same form that booked it. */
function PaymentRowMenu({
  t,
  onEdit,
  onDelete,
}: {
  t: ReturnType<typeof useT>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const close = useClosePops();

  return (
    <span className="justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onEdit();
          }}
        >
          <Pencil className="size-4" />
          {t("sales.payments.edit")}
        </button>
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}
